<style lang="scss" scoped>
.viewing {
  opacity: 0.7;
  pointer-events: none;
  cursor: not-allowed;
}

.box {
  background-color: #ffffff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.08);
  padding: 56px;
  width: 100%;
  max-width: 800px;
  border-radius: 4px;
  margin-bottom: 24px;
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  .top {
    margin-bottom: 24px;
  }
  .title {
    color: #333333;
    font-family: Lato;
    font-size: 24px;
    font-weight: 900;
    letter-spacing: 0;
    line-height: 29px;
  }
  .subtitle {
    color: #333333;
    font-family: 'Open Sans';
    font-size: 12px;
    letter-spacing: 0;
    line-height: 17px;
  }
  .logo {
    max-height: 50px;
  }

  ::v-deep .form-style {
    .form-group {
      margin-bottom: 16px;
    }
    label {
      color: #787984;
      font-family: Lato !important;
      font-size: 14px !important;
      letter-spacing: 0 !important;
      line-height: 17px !important;
      margin-bottom: 8px !important;
    }
    input,
    textarea {
      font-family: Lato !important;
      font-size: 16px !important;
      letter-spacing: 0 !important;
      line-height: 19px !important;
    }
  }
  .dark-label {
    color: #333333;
    font-family: Lato;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 24px;
    margin-bottom: 8px;
  }
}
::v-deep .el-template-dropdown {
  position: absolute;
  top: 10px;
  right: 10px;
}
</style>

<template>
  <div :class="{ viewing: viewing || $root.isMobile || (localValue.permissions && !localValue.permissions.can_edit) }">
    <div class="box position-relative">
      <el-template-dropdown v-if="!viewing && !$root.isMobile && localValue.permissions && localValue.permissions.can_edit && localValue.hasOwnProperty('id')" :objectId="objectId" :answerId="localValue.id" :template="data" type="templateAnswer" />
      <div class="top d-flex flex-row justify-content-between">
        <div class="pr-40">
          <p class="title mb-8">{{ data.title }}</p>
          <p class="subtitle mb-0">{{ data.subtitle }}</p>
        </div>
        <template v-if="data.hasOwnProperty('logo_preview') && (Array.isArray(data.logo_preview) && data.logo_preview.length !== 0)">
          <img class="logo" :src="data.logo_preview[0]" alt="Appvalvas - template - logo" />
        </template>
        <template v-else-if="defaultImage">
          <img class="logo" :src="defaultImage" alt="Appvalvas - template - logo" />
        </template>
      </div>

      <el-form class="w-100 mb-n15" @submit="submit('answerBasicInfo')" :error="server_error" scope="answerBasicInfo" :loading="loading" :success="success" :hideFooter="viewing || $root.isMobile || (localValue.permissions && !localValue.permissions.can_edit)">
        <div class="form-row form-style mb-15">
          <el-input v-if="data.reusable" v-model="localValue.template_title" @change="handleTemplateChanged" class="col-6" type="text" placeholder="Original version" scope="answerBasicInfo" name="template_title" />
          <el-input v-if="data.date" v-model="localValue.date" @change="handleTemplateChanged" type="date" class="col-6" rule="required" scope="answerBasicInfo" name="template_input_date" />
          <el-input v-if="data.company" v-model="localValue.company" @change="handleTemplateChanged" class="col-6" rule="required" scope="answerBasicInfo" name="template_input_company" />
          <el-input v-if="data.participants" v-model="localValue.participants" @change="handleTemplateChanged" class="col-6" type="text" rule="required" scope="answerBasicInfo" name="template_input_participants" />
          <el-input v-if="data.message" v-model="localValue.message" @change="handleTemplateChanged" :class="{ 'col-12' : data.reusable, 'col-6' : !data.reusable}" type="textarea" scope="answerBasicInfo" name="template_input_message" />
        </div>
        <template slot="footerRight">
          <div class="d-flex align-items-center justify-content-center w-100">
            <el-button :loading="loading" :success="success" :defaultLabel="$t('views.template.show.submit')" :loadingLabel="$t('views.template.show.saving')" :successLabel="$t('views.template.show.saved')"></el-button>
          </div>
        </template>
      </el-form>
    </div>
    <template-group
      v-for="(group, group_index) in filteredGroups"
      :group-index="group_index"
      :key="`group_${group_index}_version_${templateVersion}`"
      :group="group"
      :answerId="localValue.id"
      :answers="localValue.answers.filter((a) => a.question_group_id === group.id)"
      :viewing="viewing || $root.isMobile"
      :can-edit="localValue.permissions && localValue.permissions.can_edit"
      @group-changed="handleGroupChanged(group.id, $event)"
      @answered="handleGroupAnswered"
      :disabled="!viewing && localValue.hasOwnProperty('id')"
      :objectId="objectId"
    ></template-group>
  </div>
</template>

<script>
import HasForm from '@/components/forms/has-form';

export default {
  mixins: [HasForm],

  components: {
    ElForm: () => import(/* webpackChunkName: "elements" */ '@/components/forms/ElForm'),
    ElInput: () => import(/* webpackChunkName: "elements" */ '@/components/forms/ElInput'),
    ElButton: () => import(/* webpackChunkName: "elements" */ '@/components/forms/ElButton'),
    TemplateGroup: () => import(/* webpackChunkName: "elements" */ '@/components/elements/templates/template-group'),
    ElTemplateDropdown: () => import(/* webpackChunkName: "cards" */ '@/components/dropdowns/template'),
  },

  props: {
    value: {
      type: Object,
      default: () => {
        return {};
      },
    },
    data: {
      type: Object,
      required: true,
    },
    viewing: {
      type: Boolean,
      default: true,
    },
    objectId: {
      type: Number,
      required: false,
    },
    answerVersionId: {
      type: Number,
      required: false,
    },
    defaultImage: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
  },

  data() {
    return {
      localValue: this.value,
      templateVersion: 1,
    };
  },

  methods: {
    submit(scope) {
      if (!this.objectId) return;

      this.$validator.validateAll(scope).then((res) => {
        if (res) {
          this.loading = true;
          if (this.answerVersionId) {
            this.localValue.template_answer_version_id = this.answerVersionId;
          }
          this.$store.dispatch(`template/${scope}`, { objectId: this.objectId, templateId: this.data.id, data: this.localValue }).then((answer) => {
            this.$emit('answered', answer);
            this.loading = false;
          });
        }
      });
    },
    handleTemplateChanged() {
      this.$emit('input', this.localValue);
    },
    handleGroupChanged(group_id, data) {
      Object.keys(data).forEach((question_id) => {
        let index = this.localValue.answers.findIndex((a) => a.question_id === parseInt(question_id));
        if (index !== -1) this.localValue.answers[index].answer = data[question_id].answer;
      });
      this.$emit('input', this.localValue);
    },
    handleGroupAnswered(answers) {
      answers.forEach((new_answer) => {
        let index = this.localValue.answers.findIndex((a) => a.question_id === parseInt(new_answer.question_id));
        if (index !== -1) {
          new_answer.dirty = false;
          new_answer.type = this.localValue.answers[index].type;
          new_answer.subType = this.localValue.answers[index].subType;
          this.$set(this.localValue.answers, index, new_answer);
        }
      });
      this.$emit('input', this.localValue);
      this.templateVersion = Math.floor(Math.random() * 101);
      this.$toasted.show(this.toastHtml(this.$t('toast.template.updated')), {
        theme: 'outline',
        position: 'bottom-right',
        duration: 5000,
      });
    },
  },

  computed: {
    filteredGroups() {
      if (!this.data.hasOwnProperty('groups')) return [];
      return this.data.groups.filter((g) => g.title !== '');
    },
  },

  watch: {
    value: function() {
      this.localValue = this.value;
      this.$forceUpdate();
    },
  },
};
</script>
